import {
  isRenderableMediaField,
  Link,
  Media,
  PortableText,
  SectionWrapper,
} from '@portfolio/components'
import { OpenNewTab } from '@portfolio/icons'
import { SectionCallToActionProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'

const SectionCallToActionContainer = styled('div', {
  display: 'grid',
  gap: 32,
  gridTemplateColumns: '1fr',
  paddingInline: '20px',

  '@lg': {
    maxWidth: '$containerMaxWidthForCenteredSection',
    marginInline: 'auto',
  },

  variants: {
    alignment: {
      stretch: {
        '@lg': {
          gridTemplateColumns: '1fr 1fr',
        },
      },
      center: {
        '@lg': {
          gridTemplateColumns: '1fr',
          justifyItems: 'center',
        },
      },
    },
  },
})

const SectionCallToActionTextContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  justifyContent: 'center',
  gap: 32,
  gridRow: 2,
  '@lg': {
    gridRow: 1,
  },

  variants: {
    alignment: {
      left: {
        '@lg': {
          alignItems: 'flex-start',
          textAlign: 'left',
          gap: 24,
        },
      },
      center: {
        '@lg': {
          alignItems: 'center',
          textAlign: 'center',
        },
      },
    },
  },
})

const SectionCallToActionTitle = styled('h2', {
  color: '$white',
  projectFont: 'heading02',
  textWrap: 'pretty',
  variants: {
    width: {
      wide: {
        maxWidth: '100%',
      },
      narrow: {
        maxWidth: 440,
      },
    },
  },
})

const SectionCallToActionDescription = styled('div', {
  color: '$white',
  projectFont: 'body03',
  variants: {
    width: {
      wide: {
        maxWidth: 600,
      },
      narrow: {
        maxWidth: 440,
      },
    },
  },
})

const SectionCallToActionButton = styled('div', {})

const LinkLabel = styled('span', {
  marginRight: '8px',
})

const MediaWrap = styled('div', {
  overflow: 'hidden',
  borderRadius: '8px',
  gridRow: 1,
  '@lg': {
    paddingInline: 0,
  },
})

export const SectionCallToAction = ({
  title,
  description,
  ctaLabel,
  cta,
  media,
  priority,
}: SectionCallToActionProps) => {
  const isMediaPresent = isRenderableMediaField(media)
  const textWidth = isMediaPresent ? 'narrow' : 'wide'

  return (
    <SectionWrapper type="MEDIUM">
      <SectionCallToActionContainer
        alignment={isMediaPresent ? 'stretch' : 'center'}
      >
        <SectionCallToActionTextContent
          alignment={isMediaPresent ? 'left' : 'center'}
        >
          {title && (
            <SectionCallToActionTitle
              as={priority ? 'h1' : 'h2'}
              width={textWidth}
            >
              {title}
            </SectionCallToActionTitle>
          )}
          {description && (
            <SectionCallToActionDescription width={textWidth}>
              <PortableText
                value={description}
                appearance="plain"
                disableTopBottomMargins
              />
            </SectionCallToActionDescription>
          )}
          {cta && ctaLabel && (
            <SectionCallToActionButton>
              <Link {...cta} appearance="containedTransparent">
                <LinkLabel>{ctaLabel}</LinkLabel>
                <OpenNewTab />
              </Link>
            </SectionCallToActionButton>
          )}
        </SectionCallToActionTextContent>
        {isMediaPresent && media?.mediaPayload && (
          <MediaWrap>
            <Media
              {...media}
              priority={priority}
              layout="responsive"
              sizes="(min-width: 740px) 560px, 90vw"
              hardcropRatio="square"
            />
          </MediaWrap>
        )}
      </SectionCallToActionContainer>
    </SectionWrapper>
  )
}
